import React, { useCallback, useEffect } from 'react';
import { useJudgementAutosave } from '../rlhfShared/useJudgementAutosave';
import PackagingRatingBar from './PackagingRatingBar';
import { PackagingReportSummary, saveRating } from './api';

export interface Draft {
  rating: number | null;
  remarks: string;
}

interface ReportRatingFormProps {
  report: PackagingReportSummary;
  draft: Draft;
  onDraftChange: (reportId: number, draft: Draft) => void;
  /** Mirrors a committed save into the page's list, so the tab tick and any revisit agree. */
  onSaved: (reportId: number, rating: number, remarks: string) => void;
  onPrev: () => void;
  onNext: () => void;
  canGoPrev: boolean;
  isLast: boolean;
}

/**
 * Scoring controls for the currently open report.
 *
 * Autosave comes from `useJudgementAutosave`, the same hook /rate and /rate2 use, rather than a
 * third implementation — losing someone's written reasoning is the worst outcome this page has,
 * and that hook is where the hard-won rules live (save only on a genuine edit, baseline adopted at
 * hydration, out-of-order writes discarded).
 *
 * One difference from /rate, forced by the schema: `rating` is NOT NULL, so there is no such thing
 * as a draft with no score. Autosave is therefore gated on a score existing; until then the typed
 * remarks are held by the PAGE (via `onDraftChange`) so switching reports never loses them.
 */
const ReportRatingForm: React.FC<ReportRatingFormProps> = ({
  report,
  draft,
  onDraftChange,
  onSaved,
  onPrev,
  onNext,
  canGoPrev,
  isLast,
}) => {
  const { rating, remarks } = draft;

  const save = useCallback(
    async (reportId: number, payload: { choice: number | null; remarks: string }) => {
      if (payload.choice === null) return; // nothing to persist without a score
      await saveRating(reportId, { rating: payload.choice, remarks: payload.remarks });
      onSaved(reportId, payload.choice, payload.remarks);
    },
    [onSaved],
  );

  const { saveState, error, setBaseline, flush } = useJudgementAutosave<number | null>({
    itemId: report.id,
    choice: rating,
    remarks,
    // Only once a score exists — see the note above.
    enabled: rating !== null,
    save,
    failureMessage: 'Could not save your rating',
  });

  // Adopt the server's values as the baseline once per report, so merely opening one is never
  // mistaken for an edit (the bug the shared hook exists to prevent).
  //
  // Keyed on the report IDENTITY only — deliberately NOT on my_rating/my_remarks. A successful
  // save mirrors back into the page's list, which changes those fields; depending on them re-ran
  // this immediately after every save, and setBaseline resets the save state, so the rater's
  // "Saved" confirmation vanished the instant it appeared. The hook maintains its own baseline
  // after a commit, so re-adopting here is not just harmful but unnecessary. This component is
  // mounted with key={report.id}, so the values read here are always this report's own.
  useEffect(() => {
    setBaseline(report.id, report.my_rating, report.my_remarks ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.id, setBaseline]);

  // Navigate ONLY if the save actually landed. Advancing on a failed save would carry the rater
  // away from work that was never persisted — and the destination's own baseline would replace the
  // error indicator, so they would never learn it was lost. This is the bug /rate already had.
  const handleNext = useCallback(async () => {
    if (await flush(false)) onNext();
  }, [flush, onNext]);

  const handlePrev = useCallback(async () => {
    if (await flush(false)) onPrev();
  }, [flush, onPrev]);

  return (
    <PackagingRatingBar
      rating={rating}
      remarks={remarks}
      onRatingChange={(value) => onDraftChange(report.id, { rating: value, remarks })}
      onRemarksChange={(value) => onDraftChange(report.id, { rating, remarks: value })}
      onPrev={() => void handlePrev()}
      onNext={() => void handleNext()}
      canGoPrev={canGoPrev}
      isLast={isLast}
      busy={saveState === 'saving'}
      saveState={saveState}
      error={error}
    />
  );
};

export default ReportRatingForm;
